import { createRouter, createWebHistory } from 'vue-router';
// import { nextTick } from 'vue';
import defineAbilitiesFor from '@/plugins/ability';
import configuration from '@/configurations';
import store from '@/store';
import cookies from 'vue-cookies';
import {
  GLOBAL,
} from '@/constants/abilities';
import {
  CUSTOM_CAPTURE_EVENTS,
  LICENSE_CONSTANTS,
} from '@/constants';

const routes = [
  {
    path: '/login',
    name: 'LoginPage',
    component: () => import('@/components/pages/LoginPage.vue'),
    meta: {
      layout: 'LoginLayout',
      requires_auth: false,
    },
  },
  {
    path: '/oauth/authorize',
    name: 'OAuthAuthorizePage',
    component: () => import('@/components/pages/oauth/OAuthAuthorizePage.vue'),
    meta: {
      layout: 'LoginLayout',
      requires_auth: false,
    },
  },
  {
    path: '/oauth/error',
    name: 'OAuthErrorPage',
    component: () => import('@/components/pages/oauth/OAuthErrorPage.vue'),
    meta: {
      layout: 'LoginLayout',
      requires_auth: false,
    },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPasswordPage',
    component: () => import('@/components/pages/NewPasswordPage.vue'),
    props: {
      page_mode: 'reset',
    },
    meta: {
      layout: 'LoginLayout',
      requires_auth: false,
    },
  },
  {
    path: '/create-password',
    name: 'CreatePasswordPage',
    component: () => import('@/components/pages/NewPasswordPage.vue'),
    props: {
      page_mode: 'create',
    },
    meta: {
      layout: 'LoginLayout',
      requires_auth: false,
    },
  },
  {
    path: '/unsubscribe',
    name: 'UnsubscribePage',
    component: () => import('@/components/pages/UnsubscribePage.vue'),
    meta: {
      layout: 'LoginLayout',
      requires_auth: false,
    },
  },
  {
    path: '/design-list',
    name: 'DesignListV2Page',
    component: () => import('@/components/pages/DesignListV2Page.vue'),
    meta: {
      layout: 'DefaultLayout',
      requires_auth: false,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/request-info',
    name: 'RequestInfoPage',
    component: () => import('@/components/pages/RequestInfoPage.vue'),
    meta: {
      layout: 'DefaultLayout',
      requires_auth: false,
    },
  },
  {
    path: '/',
    name: 'HomePage',
    component: () => import('@/components/pages/HomePage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionByLicense(to, next);
    },
  },
  {
    path: '/intra',
    name: 'IntraHomePage',
    component: () => import('@/components/pages/IntraHomePage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/business-alerts',
    name: 'BusinessAlertsPage',
    component: () => import('@/components/pages/BusinessAlertsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_BUSINESS_ALERTS',
    },
  },
  {
    path: '/conferences',
    name: 'ConferencesPage',
    component: () => import('@/components/pages/ConferencesPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'CONFERENCES',
    },
  },
  {
    path: '/campaigns',
    redirect: '/campaigns/results',
  },
  {
    path: '/campaigns/results',
    name: 'CampaignResultPage',
    component: () => import('@/components/pages/campaigns/CampaignResultPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/campaigns/lists/:status',
    name: 'CampaignListPage',
    component: () => import('@/components/pages/campaigns/CampaignListPage.vue'),
    props: (route) => ({
      ...route.params,
      page_type: route.params.status,
    }),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/campaign-details/:campaign_id',
    name: 'CampaignDetailsPage',
    component: () => import('@/components/pages/campaigns/CampaignDetailsPage.vue'),
    props(route) {
      const props = { ...route.params, ...route.query };

      props.campaign_id = Number(props.campaign_id);

      return props;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_CAMPAIGN_HOME',
    },
    children: [
      {
        path: 'results',
        component: () => import('@/components/elements/campaigns/CampaignResultsElement.vue'),
        name: 'CampaignDetailsResultsTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_CAMPAIGN_HOME',
        },
      },
      {
        path: 'settings',
        component: () => import('@/components/elements/campaigns/CampaignSettingsElement.vue'),
        name: 'CampaignDetailsSettingsTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_CAMPAIGN_HOME',
        },
      },
      {
        path: 'content',
        component: () => import('@/components/elements/campaigns/CampaignContentElement.vue'),
        name: 'CampaignDetailsContentTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_CAMPAIGN_HOME',
        },
      },
      {
        path: 'targets',
        component: () => import('@/components/elements/campaigns/CampaignTargetsElement.vue'),
        name: 'CampaignDetailsTargetsTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_CAMPAIGN_HOME',
        },
      },
      {
        path: 'summary',
        component: () => import('@/components/elements/campaigns/CampaignSummaryElement.vue'),
        name: 'CampaignDetailsSummaryTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_CAMPAIGN_HOME',
        },
      },
    ],
  },
  {
    path: '/sequences',
    redirect: '/sequences/results',
  },
  {
    path: '/sequences/results',
    name: 'SequenceResultPage',
    component: () => import('@/components/pages/sequences/SequenceResultPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionSequence(to, next);
    },
  },
  {
    path: '/sequences/lists/:status',
    name: 'SequenceListPage',
    component: () => import('@/components/pages/sequences/SequenceListPage.vue'),
    props: (route) => ({
      ...route.params,
      page_type: route.params.status,
    }),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionSequenceListToV2(next);
    },
  },
  {
    path: '/sequences/list-v2',
    name: 'SequenceListV2Page',
    component: () => import('@/components/pages/sequencesV2/SequenceListV2Page.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/sequences/multi-canal/results',
    name: 'SequenceResultPageV2',
    component: () => import('@/components/pages/sequencesV2/SequenceResultPageV2.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionSequence(to, next);
    },
  },
  {
    path: '/sequences/multi-canal/settings',
    name: 'SequenceMultiCanalSettingsPage',
    component: () => import('@/components/pages/sequencesV2/SequenceSettingPageV2.vue'),
    props(route) {
      return {
        page_type: 'settings',
        sender_user_id: route.query.sender_user_id ? Number(route.query.sender_user_id) : null,
      };
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionSequence(to, next);
    },
  },
  {
    path: '/sequences/multi-canal/:sequence_id',
    component: () => import('@/components/pages/sequencesV2/SequenceMultiCanalPage.vue'),
    props(route) {
      const props = { ...route.params, ...route.query };

      props.sequence_id = Number(props.sequence_id);

      return props;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionSequence(to, next);
    },
    children: [
      {
        path: '',
        redirect: (to) => ({
          name: 'SequenceMultiCanalStepsTab',
          params: to.params,
          query: to.query,
        }),
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: 'steps',
        component: () => import('@/components/elements/sequencesV2/SequenceV2StepTabElement.vue'),
        name: 'SequenceMultiCanalStepsTab',
        props(route) {
          return {
            sequence_id: Number(route.params.sequence_id),
          };
        },
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: 'settings',
        component: () => import('@/components/elements/sequencesV2/SequenceV2ParamsElement.vue'),
        name: 'SequenceMultiCanalSettingsTab',
        props(route) {
          return {
            sequence_id: Number(route.params.sequence_id),
          };
        },
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: 'targets',
        component: () => import('@/components/elements/sequencesV2/SequenceV2TargetsElement.vue'),
        name: 'SequenceMultiCanalTargetsTab',
        props(route) {
          return {
            sequence_id: Number(route.params.sequence_id),
          };
        },
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: 'record',
        component: () => import('@/components/elements/sequencesV2/SequenceV2RecordsElement.vue'),
        name: 'SequenceMultiCanalRecordsTab',
        props(route) {
          return {
            sequence_id: Number(route.params.sequence_id),
          };
        },
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
    ],
  },
  {
    path: '/sequence-details/:sequence_id',
    name: 'SequenceDetailsPage',
    component: () => import('@/components/pages/sequences/SequenceDetailsPage.vue'),
    props(route) {
      const props = { ...route.params, ...route.query };

      props.sequence_id = Number(props.sequence_id);
      props.step_position = Number(props.step_position);

      return props;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionSequence(to, next);
    },
    children: [
      {
        path: 'steps',
        component: () => import('@/components/elements/sequences/SequenceStepsElement.vue'),
        name: 'SequenceDetailsStepsTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: 'settings',
        component: () => import('@/components/elements/sequences/SequenceParamsElement.vue'),
        name: 'SequenceDetailsSettingsTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: 'targets',
        component: () => import('@/components/elements/sequences/SequenceTargetListElement.vue'),
        name: 'SequenceDetailsTargetsTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: 'record',
        component: () => import('@/components/elements/sequences/SequenceRecordElement.vue'),
        name: 'SequenceDetailsRecordTab',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
    ],
  },
  {
    path: '/sequences/settings',
    name: 'SequenceSettingsPage',
    component: () => import('@/components/pages/sequences/SequenceSettingPage.vue'),
    props: {
      page_type: 'settings',
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
    beforeEnter: (to, from, next) => {
      return redirectionSequence(to, next);
    },
  },
  {
    path: '/conference/:conference_id/program',
    name: 'ConferenceProgramPage',
    component: () => import('@/components/pages/ConferenceProgramPage.vue'),
    props(route) {
      const props = { ...route.params, ...route.query };

      props.conference_id = Number(props.conference_id);

      return props;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/reports',
    name: 'ReportsPage',
    component: () => import('@/components/pages/ReportsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/personal-space/bat',
    name: 'BatPage',
    component: () => import('@/components/pages/personal-space/BatPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/company-segments',
    name: 'CompanySegmentsPage',
    component: () => import('@/components/pages/personal-space/CompanySegmentsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_ACCOUNT_SEGMENTS',
    },
  },
  {
    path: '/personal-space/contact-segments',
    name: 'ContactSegmentsPage',
    component: () => import('@/components/pages/personal-space/ContactSegmentsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_CONTACT_SEGMENTS',
    },
  },
  {
    path: '/personal-space/credit-consumption',
    name: 'CreditConsumptionPage',
    component: () => import('@/components/pages/personal-space/CreditConsumptionPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_CREDIT_CONSUMPTION',
    },
  },
  {
    path: '/personal-space/change-password',
    name: 'ChangePasswordPage',
    component: () => import('@/components/pages/personal-space/ChangePasswordPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_CHANGE_PASSWORD',
    },
  },
  {
    path: '/personal-space/connections',
    name: 'ConnectionsPage',
    component: () => import('@/components/pages/personal-space/ConnectionsPage.vue'),
    redirect: { name: 'EmailConnectionPage' },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
    },
    children: [
      {
        path: '/personal-space/email-connection',
        name: 'EmailConnectionPage',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
      {
        path: '/personal-space/linkedin-connection',
        name: 'LinkedinConnectionPage',
        meta: {
          layout: 'AppLayout',
          requires_auth: true,
          resource: 'PAGE_SEQUENCE_HOME',
        },
      },
    ],
  },
  {
    path: '/personal-space/profile',
    name: 'MyProfilePage',
    component: () => import('@/components/pages/personal-space/MyProfilePage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_PROFILE_AND_AI',
    },
  },
  {
    path: '/personal-space/my-contract',
    name: 'MyContractPage',
    component: () => import('@/components/pages/personal-space/MyContractPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/personal-space/my-ai',
    name: 'MyAiPage',
    component: () => import('@/components/pages/personal-space/MyAiPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/personal-space/exports',
    name: 'MyExportsPage',
    component: () => import('@/components/pages/personal-space/MyExportsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_EXPORT',
    },
  },
  {
    path: '/personal-space/notifications/settings',
    name: 'MyNotificationsSettingsPage',
    component: () => import('@/components/pages/personal-space/MyNotificationsSettingsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_NOTIFICATIONS_SETTINGS',
    },
  },
  {
    path: '/personal-space/exports/:export_id',
    name: 'MyExportPage',
    component: () => import('@/components/pages/personal-space/MyExportsPage.vue'),
    props(route) {
      const props = { ...route.params, ...route.query };

      props.export_id = Number(props.export_id);

      return props;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_EXPORT',
    },
  },
  {
    path: '/personal-space/saved-searches',
    name: 'MySavedSearchPage',
    component: () => import('@/components/pages/personal-space/MySavedSearchPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_SAVED_SEARCHES',
    },
  },
  {
    path: '/personal-space/alerts',
    name: 'MyAlertPage',
    component: () => import('@/components/pages/personal-space/MyAlertPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_MY_ALERTS',
    },
  },
  {
    path: '/personal-space/notifications',
    name: 'NotificationsPage',
    component: () => import('@/components/pages/personal-space/NotificationsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_NOTIFICATIONS',
    },
  },
  {
    path: '/personal-space/templates',
    name: 'TemplatesPage',
    component: () => import('@/components/pages/personal-space/TemplatesPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/templates/edit/:template_id?',
    name: 'EditTemplatePage',
    component: () => import('@/components/pages/personal-space/EditTemplatePage.vue'),
    props(route) {
      const props = { ...route.params, ...route.query };

      props.template_id = Number(props.template_id);

      return props;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/signatures',
    name: 'SignaturesPage',
    component: () => import('@/components/pages/personal-space/SignaturesPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/signatures-v2',
    name: 'SignaturesV2Page',
    component: () => import('@/components/pages/personal-space/SignaturesV2Page.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
    children: [
      {
        path: '/personal-space/signatures-v2/current',
        component: () => import('@/components/elements/signatures/CurrentSignatureElement.vue'),
        name: 'CurrentSignatureElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
      {
        path: '/personal-space/signatures-v2/new',
        component: () => import('@/components/elements/signatures/NewSignatureElement.vue'),
        name: 'NewSignatureElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
    ],
  },
  {
    path: '/personal-space/signatures-v2/create-from-template',
    name: 'SignaturesV2CreateFromTemplatePage',
    component: () => import('@/components/pages/personal-space/SignaturesV2CreateFromTemplatePage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/signatures-v2/create-from-email',
    name: 'SignaturesV2CreateFromEmailPage',
    component: () => import('@/components/pages/personal-space/SignaturesV2CreateFromEmailPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/signatures-v2/create-from-raw-html',
    name: 'SignaturesV2CreateFromRawHtmlPage',
    component: () => import('@/components/pages/personal-space/SignaturesV2CreateFromRawHtmlPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/signatures-v2/modify',
    name: 'SignaturesV2ModifyPage',
    component: () => import('@/components/pages/personal-space/SignaturesV2ModifyPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
  },
  {
    path: '/personal-space/other-signatures',
    name: 'OtherSignaturesPage',
    component: () => import('@/components/pages/personal-space/OtherSignaturesPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_TEMPLATES',
    },
    children: [
      {
        path: '/personal-space/other-signatures/:user_id/current',
        component: () => import('@/components/elements/signatures/OtherSignatureCurrentElement.vue'),
        name: 'OtherSignatureCurrentElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
      {
        path: '/personal-space/other-signatures/:user_id/new',
        component: () => import('@/components/elements/signatures/OtherSignatureNewElement.vue'),
        name: 'OtherSignatureNewElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
      {
        path: '/personal-space/other-signatures/:user_id/create-from-template',
        component: () => import('@/components/elements/signatures/OtherSignatureCreateFromTemplateElement.vue'),
        name: 'OtherSignatureCreateFromTemplateElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
      {
        path: '/personal-space/other-signatures/:user_id/create-from-email',
        component: () => import('@/components/elements/signatures/OtherSignatureCreateFromEmailElement.vue'),
        name: 'OtherSignatureCreateFromEmailElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
      {
        path: '/personal-space/other-signatures/:user_id/create-from-raw-html',
        component: () => import('@/components/elements/signatures/OtherSignatureCreateFromRawHtmlElement.vue'),
        name: 'OtherSignatureCreateFromRawHtmlElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
      {
        path: '/personal-space/other-signatures/:user_id/modify',
        component: () => import('@/components/elements/signatures/OtherSignatureModifyElement.vue'),
        name: 'OtherSignatureModifyElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_TEMPLATES',
        },
      },
    ],
  },
  {
    path: '/activate-account',
    name: 'ActivateAccountPage',
    component: () => import('@/components/pages/ChangePasswordDisconnectedPage.vue'),
    props: {
      account_activation: true,
    },
    meta: {
      layout: 'DefaultLayout',
      requires_auth: false,
      resource: 'PAGE_CHANGE_PASSWORD',
    },
    beforeEnter: async (to, from, next) => {
      const {
        token,
      } = to.query;

      if (token) {
        try {
          const {
            status,
          } = await store.dispatch(
            'user/checkToken',
            {
              token,
            }
          );

          if (status === 'unknown') {
            return next({
              path: '/account-already-activated',
            });
          }
        } catch (error) {
          console.error('check-token error', error);
        }
      }

      return next();
    },
  },
  {
    path: '/account-already-activated',
    name: 'AccountAlreadyActivatedPage',
    component: () => import('@/components/pages/AccountAlreadyActivatedPage.vue'),
    meta: {
      layout: 'DefaultLayout',
      requires_auth: false,
    },
  },
  {
    path: '/change-password',
    name: 'ChangePasswordDisconnectedPage',
    component: () => import('@/components/pages/ChangePasswordDisconnectedPage.vue'),
    meta: {
      layout: 'DefaultLayout',
      requires_auth: false,
      resource: 'PAGE_CHANGE_PASSWORD',
    },
  },
  {
    path: '/email-signature-generator',
    name: 'EmailSignatureGeneratorPage',
    component: () => import('@/components/pages/emails/EmailSignatureGeneratorPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_EMAIL_SIGNATURE_GENERATOR',
    },
  },
  {
    path: '/campaigns/settings',
    name: 'CampaignSettingsPage',
    component: () => import('@/components/pages/campaigns/CampaignSettingPage.vue'),
    props: {
      page_type: 'settings',
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PAGE_SEQUENCE_HOME',
    },
  },
  {
    path: '/my-prospecting/dashboard',
    name: 'DashboardPage',
    component: () => import('@/components/pages/my-prospecting/DashboardPage.vue'),
    redirect: '/my-prospecting/dashboard/synthesis',
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_DASHBOARD',
    },
    children: [
      {
        path: 'synthesis',
        component: () => import('@/components/elements/my-prospecting/dashboard/DashboardSynthesisTabElement.vue'),
        name: 'DashboardSynthesisTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
      {
        path: 'consulted-cards',
        component: () => import('@/components/elements/my-prospecting/dashboard/DashboardConsultedCardsTabElement.vue'),
        name: 'DashboardConsultedCardsTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
      {
        path: 'connections',
        component: () => import('@/components/elements/my-prospecting/dashboard/DashboardConnectionsTabElement.vue'),
        name: 'DashboardConnectionsTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
    ],
  },
  {
    path: '/my-prospecting/my-prospects',
    name: 'MyProspectsPage',
    component: () => import('@/components/pages/my-prospecting/MyProspectsPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_DASHBOARD',
    },
  },
  {
    path: '/my-prospecting/smart-scoring',
    name: 'SmartScoringPage',
    component: () => import('@/components/pages/my-prospecting/SmartScoringPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_DASHBOARD',
    },
    children: [
      {
        path: 'customers-analysis',
        component: () => import('@/components/elements/my-prospecting/smart-scoring/CustomerAnalysisTabElement.vue'),
        name: 'CustomerAnalysisTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
    ],
  },
  {
    path: '/my-prospecting/team-monitoring',
    name: 'TeamMonitoringPage',
    component: () => import('@/components/pages/my-prospecting/TeamMonitoringPage.vue'),
    redirect: '/my-prospecting/team-monitoring/synthesis',
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_DASHBOARD',
    },
    children: [
      {
        path: 'synthesis',
        component: () => import('@/components/elements/my-prospecting/team-monitoring/TeamMonitoringSynthesisTabElement.vue'),
        name: 'TeamMonitoringSynthesisTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
      {
        path: 'consulted-cards',
        component: () => import('@/components/elements/my-prospecting/team-monitoring/TeamMonitoringConsultedCardsTabElement.vue'),
        name: 'TeamMonitoringConsultedCardsTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
      {
        path: 'connections',
        component: () => import('@/components/elements/my-prospecting/team-monitoring/TeamMonitoringConnectionsTabElement.vue'),
        name: 'TeamMonitoringConnectionsTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
      {
        path: 'individual-monitoring',
        component: () => import('@/components/elements/my-prospecting/team-monitoring/TeamMonitoringIndividualMonitoringTabElement.vue'),
        name: 'TeamMonitoringIndividualMonitoringTabElement',
        meta: {
          requires_auth: true,
          resource: 'PERSONAL_SPACE_DASHBOARD',
        },
      },
    ],
  },
  {
    path: '/my-prospecting/lists-monitoring',
    name: 'ListMonitoringPage',
    component: () => import('@/components/pages/my-prospecting/ListMonitoringPage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'PERSONAL_SPACE_DASHBOARD',
    },
  },
  {
    path: '/contact/:contact_id',
    name: 'ContactPage',
    component: () => import('@/components/pages/contact/ContactPage.vue'),
    redirect: (to) => {
      return `/contact/${to.params.contact_id}/profile`;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'CONTACT_PAGE',
    },
    children: [
      {
        name: 'ContactPageRoot',
        path: '',
        redirect: (to) => {
          return `/contact/${to.params.contact_id}/profile`;
        },
      },
      {
        path: 'profile',
        component: () => import('@/components/elements/contact/ContactProfileElement.vue'),
        name: 'ContactProfileElement',
        meta: {
          requires_auth: true,
          resource: 'CONTACT_PAGE',
        },
      },
      {
        path: 'other-mandates',
        component: () => import('@/components/elements/contact/ContactOtherMandatesElement.vue'),
        name: 'ContactOtherMandatesElement',
        meta: {
          requires_auth: true,
          resource: 'CONTACT_PAGE',
        },
      },
      {
        path: 'history',
        component: () => import('@/components/elements/contact/ContactHistoryElement.vue'),
        name: 'ContactHistoryElement',
        meta: {
          requires_auth: true,
          resource: 'CONTACT_PAGE',
        },
      },
    ],
  },
  {
    path: '/contact/:contact_id/engage',
    name: 'ContactEngagePage',
    component: () => import('@/components/pages/contact/ContactEngagePage.vue'),
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'CONTACT_PAGE',
    },
  },
  {
    path: '/account/:account_id',
    name: 'AccountPage',
    component: () => import('@/components/pages/AccountPage.vue'),
    redirect: (to) => {
      return `/account/${to.params.account_id}/summary`;
    },
    meta: {
      layout: 'AppLayout',
      requires_auth: true,
      resource: 'ACCOUNT_PAGE',
    },
    children: [
      {
        name: 'AccountPageRoot',
        path: '',
        redirect: (to) => {
          return `/account/${to.params.account_id}/summary`;
        },
      },
      {
        path: 'summary',
        component: () => import('@/components/elements/account/AccountSummaryElement.vue'),
        name: 'AccountSummaryElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
      {
        path: 'news',
        component: () => import('@/components/elements/account/AccountNewsElement.vue'),
        name: 'AccountNewsElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
      {
        path: 'news/press/:press_id',
        component: () => import('@/components/elements/account/PressNewsElement.vue'),
        name: 'PressNewsElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
      {
        path: 'news/business-opportunity/:business_opportunity_id',
        component: () => import('@/components/elements/account/BusinessOpportunityNewsElement.vue'),
        name: 'BusinessOpportunityNewsElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
      {
        path: 'contacts',
        component: () => import('@/components/elements/account/AccountContactsElement.vue'),
        redirect: (to) => {
          return `/account/${to.params.account_id}/contacts/chart`;
        },
        name: 'AccountContactsElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
        children: [
          {
            name: 'AccountContactsElementRoot',
            path: '',
            redirect: (to) => {
              return `/account/${to.params.account_id}/contacts/chart`;
            },
          },
          {
            path: 'chart',
            component: () => import('@/components/elements/account/AccountContactsChartElement.vue'),
            name: 'AccountContactsChartElement',
            meta: {
              requires_auth: true,
              resource: 'ACCOUNT_PAGE',
            },
          },
          {
            path: 'top',
            component: () => import('@/components/elements/account/AccountTopContactsElement.vue'),
            name: 'AccountTopContactsElement',
            meta: {
              requires_auth: true,
              resource: 'ACCOUNT_PAGE',
            },
          },
          {
            path: 'new',
            component: () => import('@/components/elements/account/AccountContactsNewElement.vue'),
            name: 'AccountContactsNewElement',
            meta: {
              requires_auth: true,
              resource: 'ACCOUNT_PAGE',
            },
          },
        ],
      },
      {
        path: 'subsidiaries',
        component: () => import('@/components/elements/account/AccountSubsidiariesElement.vue'),
        name: 'AccountSubsidiariesElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
      {
        path: 'legal',
        component: () => import('@/components/elements/account/AccountLegalElement.vue'),
        name: 'AccountLegalElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
      {
        path: 'advertising',
        component: () => import('@/components/elements/account/AccountAdvertisingElement.vue'),
        name: 'AccountAdvertisingElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
      {
        path: 'history',
        component: () => import('@/components/elements/account/AccountHistoryElement.vue'),
        name: 'AccountHistoryElement',
        meta: {
          requires_auth: true,
          resource: 'ACCOUNT_PAGE',
        },
      },
    ],
  },
];

const router = createRouter({
  base: configuration.base_url,
  history: createWebHistory(),
  mode: 'history',
  routes,
});

const getDefaultRouteByLicense = (license_id) => {
  if (
    license_id === LICENSE_CONSTANTS.SENDER.ID
    || license_id === LICENSE_CONSTANTS.SENDER_V2.ID
  ) {
    return '/personal-space/email-connection';
  }

  if (license_id === LICENSE_CONSTANTS.CONNECTOR.ID) {
    return '/personal-space/credit-consumption';
  }

  if (license_id === LICENSE_CONSTANTS.INTRANET.ID
    && store.getters['user/userIsAlpha']
  ) {
    return '/intra';
  }

  return '/';
};

router.beforeEach(async (to, from, next) => {
  if (to.fullPath.includes('//')) {
    const corrected_path = to.fullPath.replace(/\/{2,}/g, '/');

    return next({ path: corrected_path, replace: true });
  }

  const redirect = await store.dispatch('user/checkJwtFromUrl');

  if (redirect) {
    const query = { ...to.query };

    delete query.jwt;
    delete query.jwt_external;

    const params = new URLSearchParams(query).toString();
    const new_url = `${window.location.pathname}${params ? `?${params}` : ''}`;

    window.history.replaceState(null, '', new_url);

    return next({
      path: to.path,
      query: query,
      replace: true,
    });
  }

  if (to.meta.requires_auth === false) {
    // If user is already logged in, redirect to root
    if (to.path === '/login'
      && (cookies.get(GLOBAL.COOKIE.JWT) || cookies.get(GLOBAL.COOKIE.CONNECTED_AS))
    ) {
      return next('/');
    }

    return next();
  }

  // I moved next line here to avoid redirectioning by logout in checkJwtFromCookies action
  // ex: login page, design list page are public pages
  await store.dispatch('user/checkJwtFromCookies');
  await store.dispatch('user/loginWithJwtFromCookies');

  const is_connection_tab_navigation = ['EmailConnectionPage', 'LinkedinConnectionPage'].includes(to.name)
    && ['EmailConnectionPage', 'LinkedinConnectionPage'].includes(from.name);

  if (is_connection_tab_navigation && Object.keys(store.getters['user/tokenData']).length !== 0) {
    store.dispatch(
      'event_capture/captureRouteChange',
      {
        event: CUSTOM_CAPTURE_EVENTS.PAGELEAVE,
      }
    );

    return next();
  }

  // Put a protection to avoid calling authenticated API without token
  if (Object.keys(store.getters['user/tokenData']).length !== 0) {
    await store.dispatch('user/getUserData');
    await store.dispatch('user/searchOrganizationUserList');
    await store.dispatch('user/searchColleagueUserList');

    const abilities = defineAbilitiesFor(store.getters['user/userData']);

    const can_navigate = to.matched.some((route) => {
      const is_alpha_route = route.meta.requires_alpha;
      const is_beta_route = route.meta.requires_beta;
      const user_is_alpha = store.getters['user/userIsAlpha'];
      const user_is_beta = store.getters['user/userIsBeta'];

      if (is_beta_route) {
        if (!user_is_alpha && !user_is_beta) {
          return false;
        }
      }

      if (is_alpha_route && !user_is_alpha) {
        return false;
      }

      return abilities.can('read', route.meta.resource);
    });

    if (!can_navigate) {
      return next(getDefaultRouteByLicense(
        store.getters['user/userData']?.organization_user_license_list?.[0]?.license?.id
      ));
    }
  }

  //capture pageleave event
  store.dispatch(
    'event_capture/captureRouteChange',
    {
      event: CUSTOM_CAPTURE_EVENTS.PAGELEAVE,
    }
  );

  next();
});

router.afterEach((to, from, failure) => {
  if (!failure) {
    //capture pageview event
    store.dispatch('event_capture/captureRouteChange', {
      event: CUSTOM_CAPTURE_EVENTS.PAGEVIEW,
    });
  }
});

/**
 * Redirect to the appropriate page according to the user's license
 */
const redirectionByLicense = (to, next) => {
  const user_data = store.getters['user/userData'];
  const license_id = user_data.organization_user_license_list[0].license.id;

  const redirectWithQuery = (path) => next({
    path,
    query: to.query,
  });

  if (license_id === LICENSE_CONSTANTS.SENDER.ID
      || license_id === LICENSE_CONSTANTS.SENDER_V2.ID
  ) {
    return redirectWithQuery('/personal-space/email-connection');
  } else if (license_id === LICENSE_CONSTANTS.CONNECTOR.ID) {
    return redirectWithQuery('/personal-space/credit-consumption');
  } else if (license_id === LICENSE_CONSTANTS.INSIGHT.ID
    || license_id === LICENSE_CONSTANTS.SEARCH_AND_LISTS.ID) {
    window.location.href = `${configuration.desk_v1_host}/advanced-search`;
  } else if (
    license_id === LICENSE_CONSTANTS.INTRANET.ID
    && store.getters['user/userIsAlpha'] // to remove after
  ) {
    return redirectWithQuery('/intra');
  } else if (
    license_id === LICENSE_CONSTANTS.KAM.ID
    || license_id === LICENSE_CONSTANTS.INTRANET.ID
  ) {
    return redirectWithQuery('/business-alerts');
  } else if (
    license_id === LICENSE_CONSTANTS.MARKETING.ID
      || license_id === LICENSE_CONSTANTS.SMART_CAMPAIGN_V2.ID
  ) {
    return redirectWithQuery('/campaigns/results');
  }

  next();
};

const redirectionSequence = (to, next) => {
  const user_data = store.getters['user/userData'];
  const sequence_id = to.params.sequence_id;
  const current_path = to.path;

  const path_parts = current_path.split('/').filter((part) => part);
  const sub_route = path_parts[path_parts.length - 1];
  const is_sub_route = ['steps', 'settings', 'targets', 'record', 'records'].includes(sub_route);
  const target_sub_route = is_sub_route ? sub_route : 'steps';

  if (user_data?.can_send_multichannel_sequences) {
    if (current_path.includes('/sequence-details/')) {
      return next(`/sequences/multi-canal/${sequence_id}/${target_sub_route}`);
    }

    if (current_path.includes('/sequences/settings')) {
      return next(`/sequences/multi-canal/settings`);
    }

    if (current_path.includes('/sequences/results')) {
      return next(`/sequences/multi-canal/results`);
    }
  } else {
    if (current_path.includes('/sequences/multi-canal/')) {
      return next(`/sequence-details/${sequence_id}/${target_sub_route}`);
    }

    if (current_path.includes('/sequences/multi-canal/settings')) {
      return next(`/sequences/settings`);
    }

    if (current_path.includes('/sequences/multi-canal/results')) {
      return next(`/sequences/results`);
    }
  }

  next();
};

/**
 * Redirect from regular sequence list to v2 list when user has multichannel permission
 */
const redirectionSequenceListToV2 = (next) => {
  const user_data = store.getters['user/userData'];

  if (user_data?.can_send_multichannel_sequences) {
    return next({ name: 'SequenceListV2Page', replace: true });
  }

  next();
};

export default router;
